// Contracts — vista WRITES: adding contributors, editing cards, moving people
// between admins, staff management.
import { z } from 'zod';
import {
  VistaCentsSchema,
  VistaContributorStatusSchema,
  VistaEmailSchema,
  VistaRoleSchema,
} from './enums';

// One admin adding people in a sitting. A cap rather than unbounded input: each
// address costs an INSERT plus a password hash, and a Worker request has a CPU
// budget. Bigger intakes are several batches, which is also how they arrive.
export const VISTA_ADD_BATCH = 50;

// POST /api/vista/contributors — add people to MY team.
export const VistaAddContributorsReqSchema = z.object({
  emails: z.array(VistaEmailSchema).min(1).max(VISTA_ADD_BATCH),
  name: z.string().trim().max(200).optional(),
  country: z.string().trim().length(2).optional(),
  rateCents: VistaCentsSchema.min(0).max(100_000).optional(),
});
export type VistaAddContributorsReq = z.infer<typeof VistaAddContributorsReqSchema>;

export const VistaAddContributorsResSchema = z.object({
  created: z.array(z.string()),
  existing: z.array(z.string()),
  // Cabinet credentials, ONE-TIME: generated for every created email and for
  // existing ones that had no password yet. Existing accounts with a password
  // are not touched — regenerating would lock people out silently.
  accounts: z.array(z.object({ email: z.string(), password: z.string() })),
  /** Почты, которые уже закреплены за ДРУГИМ админом. Их не забирают молча:
   *  перевод между пулами — решение менеджера, а не того, кто первым набрал
   *  чужой адрес. Пустой массив в обычном случае. */
  foreign: z.array(z.string()).default([]),
});
export type VistaAddContributorsRes = z.infer<typeof VistaAddContributorsResSchema>;

// PATCH /api/vista/contributor/:id — edit the card. All fields optional;
// explicit null clears. rateCents null means "unset", which blocks accrual
// rather than falling back to a guessed default.
//
// city/uiLang (F7): the card's own editable copy of what ingest otherwise only
// ever STAMPS from the device — a staff correction (typo, contributor moved,
// wrong onboarding language) must be possible without touching a capture row.
export const VistaPatchContributorReqSchema = z.object({
  name: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(50).nullable().optional(),
  country: z.string().trim().length(2).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  // Contributor cabinets are en/es/pt only — 'ru' is the STAFF language (see
  // migration 0001's `users.ui_lang` vs `contributors.ui_lang` defaults, and
  // the "language is limited to the contributor set" test in
  // contracts_shapes.test.ts). Do not widen this to match the staff enum.
  uiLang: z.enum(['en', 'es', 'pt']).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  rateCents: VistaCentsSchema.min(0).max(100_000).nullable().optional(),
  status: VistaContributorStatusSchema.optional(),
  // Cabinet password reset by the pool's admin (0004). No self-service.
  password: z.string().min(8).max(200).optional(),
  // F3 — привязать этот съёмочный кабинет к штабному аккаунту с такой почтой
  // (null — отвязать). Именно связка, а НЕ смена почты съёмщика: у человека
  // остаются две разные почты, а переключатель кабинетов появляется.
  linkUserEmail: z.string().trim().email().max(200).nullable().optional(),
});
export type VistaPatchContributorReq = z.infer<typeof VistaPatchContributorReqSchema>;

export const VistaOkChangedResSchema = z.object({
  ok: z.literal(true),
  changed: z.number().int(),
});
export type VistaOkChangedRes = z.infer<typeof VistaOkChangedResSchema>;

// POST /api/vista/assign — move a contributor between admins (manager only).
export const VistaAssignReqSchema = z.object({
  contributorId: z.string().min(1).max(64),
  userId: z.string().min(1).max(64),
});
export type VistaAssignReq = z.infer<typeof VistaAssignReqSchema>;

export const VistaAssignResSchema = z.object({
  ok: z.literal(true),
  // false when the person already belonged to that admin — the UI may fire the
  // call idempotently.
  changed: z.boolean(),
});
export type VistaAssignRes = z.infer<typeof VistaAssignResSchema>;

// POST /api/vista/manage/invite {contributorId} — F5: a one-time link instead
// of dictating a password over chat. Mints a magic_tokens row keyed to the
// contributor's OWN email (redeemMagicToken in auth.ts resolves it to a
// contributor session), 7-day TTL — far longer than the 15-minute staff login
// link, because this one has to survive someone opening it from a forwarded
// message days later.
export const VistaInviteContributorReqSchema = z.object({
  contributorId: z.string().min(1).max(64),
});
export type VistaInviteContributorReq = z.infer<typeof VistaInviteContributorReqSchema>;

export const VistaInviteContributorResSchema = z.object({ url: z.string() });
export type VistaInviteContributorRes = z.infer<typeof VistaInviteContributorResSchema>;

// GET /api/vista/unassigned — people the sync discovered with no owner.
// They are invisible to admins by design: silently dropping someone into a pool
// would be worse than showing managers a gap to claim.
export const VistaUnassignedRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: VistaContributorStatusSchema,
  created_at: z.number().int(),
  accepted_seconds: z.number().int(),
});

export const VistaUnassignedResSchema = z.object({
  rows: z.array(VistaUnassignedRowSchema),
});
export type VistaUnassignedRes = z.infer<typeof VistaUnassignedResSchema>;

// ── staff (manager only) ────────────────────────────────────────────────────

export const VistaStaffRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: VistaRoleSchema,
  // What WE pay this person per accepted hour of their pool (0002). Null =
  // not negotiated yet — and then the cascade gate blocks their rate edits.
  rate_cents: z.number().int().nullable(),
  show_balance: z.number().int(),
  disabled: z.number().int(),
  pool_size: z.number().int(),
});

export const VistaStaffListResSchema = z.object({ rows: z.array(VistaStaffRowSchema) });
export type VistaStaffListRes = z.infer<typeof VistaStaffListResSchema>;

export const VistaCreateStaffReqSchema = z.object({
  email: VistaEmailSchema,
  name: z.string().trim().min(1).max(200),
  role: VistaRoleSchema,
  // Required while password login is the only door: an account created without
  // one would simply be unable to sign in.
  password: z.string().min(8).max(200),
  // Ставку можно задать сразу. Без неё админ заведён, но работать не может:
  // каскад запрещает ему назначать ставки своим людям, пока не назначена его
  // собственная — и это выясняется через экран, а не при заведении.
  rateCents: VistaCentsSchema.min(0).max(100_000).nullable().optional(),
});
export type VistaCreateStaffReq = z.infer<typeof VistaCreateStaffReqSchema>;

export const VistaCreateStaffResSchema = z.object({ id: z.string(), email: z.string() });
export type VistaCreateStaffRes = z.infer<typeof VistaCreateStaffResSchema>;

export const VistaPatchStaffReqSchema = z.object({
  role: VistaRoleSchema.optional(),
  showBalance: z.boolean().optional(),
  disabled: z.boolean().optional(),
  // Manager-only in the handler. Null clears the rate and re-arms the cascade
  // gate for that admin.
  rateCents: VistaCentsSchema.min(0).max(100_000).nullable().optional(),
  // Password reset by the manager — the only reset path while email is out.
  password: z.string().min(8).max(200).optional(),
});
export type VistaPatchStaffReq = z.infer<typeof VistaPatchStaffReqSchema>;

// GET /api/vista/stats/admins — the manager screen: one row per staff member
// with their pool's standing and the money split under the cascade model.
//
// Numbers are LIVE (current rates × current hours) — the quick answer to "how
// are my admins doing". The ledger is the durable, snapshot-based record; the
// two agree only while rates stay unchanged, which is exactly why both exist.
export const VistaAdminStatsRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: VistaRoleSchema,
  rateCents: z.number().int().nullable(),
  disabled: z.boolean(),
  poolSize: z.number().int(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  // Σ contributor rate × accepted hours over pool members WITH a rate.
  contributorPayoutCents: z.number().int(),
  // Σ (admin rate − contributor rate) × accepted hours; null while the admin
  // has no rate — "no margin yet" must not read as "zero margin".
  marginCents: z.number().int().nullable(),
  // Pool members without a rate: their hours earn nobody anything, which is a
  // problem to fix, not a number to hide.
  unratedCount: z.number().int(),
});

export const VistaAdminStatsResSchema = z.object({
  rows: z.array(VistaAdminStatsRowSchema),
});
export type VistaAdminStatsRes = z.infer<typeof VistaAdminStatsResSchema>;
export type VistaAdminStatsRow = z.infer<typeof VistaAdminStatsRowSchema>;

// GET /api/vista/unrated — contributors in the caller's scope with no rate set.
// A work queue, not a report: their accepted hours cannot accrue until someone
// sets a rate, so the money quietly stops rather than erroring.
export const VistaUnratedRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  country: z.string().nullable(),
  created_at: z.number().int(),
});

export const VistaUnratedResSchema = z.object({ rows: z.array(VistaUnratedRowSchema) });
export type VistaUnratedRes = z.infer<typeof VistaUnratedResSchema>;

// POST /api/vista/_admin/bootstrap {email,name?} — X-Admin-Secret, no session.
// Solves the chicken-and-egg: someone has to create the first manager before
// any login exists. Idempotent — re-running promotes the existing row.
export const VistaBootstrapReqSchema = z.object({
  email: VistaEmailSchema,
  name: z.string().trim().min(1).max(200).optional(),
  // Lets the operator seed the first manager's password in the same call —
  // there is no one in the UI yet to do it, same chicken-and-egg as the role.
  password: z.string().min(8).max(200).optional(),
});
export type VistaBootstrapReq = z.infer<typeof VistaBootstrapReqSchema>;

// ── what the client pays us, per geography ──────────────────────────────────
// GET /api/vista/client-rates (manager) — the whole table, '*' included.
//
// A per-country table rather than one number, because the client prices by
// geography (Tim, 2026-08-10). '*' is the fallback row; a country with neither
// its own rate nor a fallback is UNPRICED, and its accepted hours are reported
// separately instead of being folded into revenue at zero.
export const VistaClientRateSchema = z.object({
  country: z.string(), // ISO 3166-1 alpha-2, or '*'
  cents: VistaCentsSchema,
  note: z.string().nullable(),
  updatedAt: z.number().int(),
});
export type VistaClientRate = z.infer<typeof VistaClientRateSchema>;

export const VistaClientRatesResSchema = z.object({ rows: z.array(VistaClientRateSchema) });
export type VistaClientRatesRes = z.infer<typeof VistaClientRatesResSchema>;

// PUT /api/vista/client-rates — upsert one geography. Manager only: this is the
// revenue side of the deal, and an admin who could edit it could rewrite our
// margin on their own team.
export const VistaSetClientRateReqSchema = z.object({
  // '*' sets the fallback. Uppercased by the worker so 'ar' and 'AR' are one row.
  country: z.string().trim().min(1).max(2),
  cents: VistaCentsSchema.min(0).max(1_000_000),
  note: z.string().trim().max(500).nullable().optional(),
});
export type VistaSetClientRateReq = z.infer<typeof VistaSetClientRateReqSchema>;

export const VistaSetClientRateResSchema = z.object({ ok: z.literal(true) });
export type VistaSetClientRateRes = z.infer<typeof VistaSetClientRateResSchema>;
